import { IconButton } from '@components/iconButton/IconButton';
import cx from 'classnames';
import type { DragEvent } from 'react';
import typography from '~styles/typography.scss';
import type { VipTier } from './apiHelpers';
import styles from './VipProgramSection.scss';
import { Icon } from '@components/icon/Icon';

export interface VipTiersTableProps {
  onDeleteVipTier: (vipTier: VipTier) => void;
  onEditVipTier: (vipTier: VipTier) => void;
  onReorderVipTiers: (vipTierIds: string[]) => void;
  vipTiers: VipTier[];
}

const formatTimeWindow = (days: number | null): string => {
  if (days === null) {
    return 'all time';
  }
  if (days === 180) {
    return 'last 6 months';
  }
  if (days === 365) {
    return 'last 12 months';
  }
  return `last ${days} days`;
};

const formatMoney = (amountCents: number): string =>
  new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(amountCents / 100);

const getCriteriaDescription = (vipTier: VipTier): string => {
  const criteria = [];

  if (vipTier.reservationCountThreshold !== null) {
    criteria.push(
      `${vipTier.reservationCountThreshold} completed reservations ${formatTimeWindow(
        vipTier.reservationCountTimeWindowDays,
      )}`,
    );
  }

  if (vipTier.spendAmountThreshold !== null) {
    criteria.push(
      `${formatMoney(vipTier.spendAmountThreshold)} spent ${formatTimeWindow(
        vipTier.spendAmountTimeWindowDays,
      )}`,
    );
  }

  return criteria.join(' or ');
};

export const VipTiersTable = ({
  onDeleteVipTier,
  onEditVipTier,
  onReorderVipTiers,
  vipTiers,
}: VipTiersTableProps) => {
  const handleDragStart = (
    event: DragEvent<HTMLTableRowElement>,
    vipTierId: string,
  ) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', vipTierId);
  };

  const handleDrop = (
    event: DragEvent<HTMLTableRowElement>,
    targetVipTierId: string,
  ) => {
    event.preventDefault();
    const sourceVipTierId = event.dataTransfer.getData('text/plain');

    if (!sourceVipTierId || sourceVipTierId === targetVipTierId) {
      return;
    }

    const reorderedVipTierIds = vipTiers.map((vipTier) => vipTier.id);
    const sourceIndex = reorderedVipTierIds.indexOf(sourceVipTierId);
    const targetIndex = reorderedVipTierIds.indexOf(targetVipTierId);

    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }

    const [movedVipTierId] = reorderedVipTierIds.splice(sourceIndex, 1);
    reorderedVipTierIds.splice(targetIndex, 0, movedVipTierId);
    onReorderVipTiers(reorderedVipTierIds);
  };

  return (
    <div className={styles.couponsTableWrapper}>
      <table className={styles.couponsTable}>
        <thead>
          <tr>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.indexCell,
              )}
            ></th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.indexCell,
              )}
            >
              #
            </th>
            <th
              className={cx(typography.t2, styles.tableHeader, styles.nameCell)}
            >
              Name
            </th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.descriptionCell,
              )}
            >
              Description
            </th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.actionsCell,
              )}
              aria-label="Actions"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {vipTiers.map((vipTier) => (
            <tr
              draggable
              key={vipTier.id}
              className={styles.clickableCouponRow}
              onClick={() => {
                onEditVipTier(vipTier);
              }}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDragStart={(event) => {
                handleDragStart(event, vipTier.id);
              }}
              onDrop={(event) => {
                handleDrop(event, vipTier.id);
              }}
            >
              <td className={cx(typography.c2, styles.dragCell)}>
                <Icon name="dragDropDots" className={styles.dragHandle} />
              </td>
              <td className={cx(typography.c2, styles.indexCell)}>
                {vipTier.index}
              </td>
              <td className={cx(typography.c2, styles.nameCell)}>
                {vipTier.name}
              </td>
              <td className={cx(typography.c2, styles.descriptionCell)}>
                {vipTier.description || getCriteriaDescription(vipTier)}
              </td>
              <td className={styles.actionsCell}>
                <div className={styles.actions}>
                  <IconButton
                    ariaLabel={`Edit ${vipTier.name}`}
                    className={styles.actionButton}
                    iconName="pencil"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditVipTier(vipTier);
                    }}
                  />
                  <IconButton
                    ariaLabel={`Delete ${vipTier.name}`}
                    className={styles.actionButton}
                    iconName="trash"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteVipTier(vipTier);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
